import filesize from 'filesize';
import humanFormat from 'human-format';
import { isFinite, isNumber, isPlainObject, isString } from 'lodash-es';
import moment from 'moment-timezone';
import React, { type JSX } from 'react';

import {
  checkEmpty,
  getUniqueId,
  toCapitalizeOrUpper,
} from 'core/services/utils';

const NEW_LINE = '\r\n';

const timeScale = new humanFormat.Scale({
  seconds: 1,
  minutes: 60,
  hours: 3600,
  days: 86400,
  months: 2592000,
});

export const formatDuration = (numSeconds: number) =>
  humanFormat(numSeconds, { scale: timeScale });

export const formatBytes = (num, round = 2) => {
  const value = parseInt(num, 10);
  if (!isFinite(value)) {
    return '';
  }

  return filesize(num, { round });
};

export const handleNewLines = (val) => {
  return typeof val === 'string' && val.indexOf(NEW_LINE) >= 0
    ? val.split(NEW_LINE).reduce((jsxLines, line, idx) => {
        if (!checkEmpty(line)) {
          jsxLines.push(
            <React.Fragment key={`line-${idx}`}>
              {line}
              <br />
            </React.Fragment>
          );
        }
        return jsxLines;
      }, [] as JSX.Element[])
    : val;
};

// complexity 27
// eslint-disable-next-line complexity
export const jsonToHtml = (entry, options) => {
  if (!entry) {
    return '';
  } else if (isNumber(entry) || isString(entry)) {
    return entry;
  } else {
    const skipKeys =
      options && isString(options.skipKeys)
        ? options.skipKeys.split(/\s?,\s?/g)
        : [];
    const skipNoVal = !!(options && options.skipNoVal);
    const keyReplaceMap = options.keyReplaceMap || {};
    const valFormatter = options.valFormatter;
    const isArrayEntry = Array.isArray(entry);
    const keys = Object.keys(entry);
    const html: JSX.Element[] = [];

    for (let i = 0, len = keys.length; i < len; i++) {
      let key = keys[i];
      if (key === '$$typeof' || skipKeys.indexOf(key) >= 0) {
        continue;
      }

      let val = entry[key];
      let isNestedTable = false;
      if (val && !isString(val) && (isPlainObject(val) || Array.isArray(val))) {
        const { preExtraRows: _pre, postExtraRows: _post, ...rest } = options;
        val = jsonToHtml(val, rest);
        isNestedTable = true;
      } else if (val === null) {
        val = '';
      } else if (typeof val === 'boolean') {
        val = val.toString();
      } else if (valFormatter) {
        val = valFormatter(key, val);
      }

      if (skipNoVal && checkEmpty(val)) {
        continue;
      }

      if (key && key.indexOf('_') >= 0) {
        key = key.replace(/_/g, ' ');
      }
      key = keyReplaceMap[key] || toCapitalizeOrUpper(key);

      html.push(
        isArrayEntry ? (
          isNestedTable ? (
            val
          ) : (
            <div key={`array-item-${i}`}>{handleNewLines(val)}</div>
          )
        ) : (
          <tr key={`row-${keys[i]}`}>
            <th>{key}</th>
            <td className={isNestedTable ? 'hasNestedValues' : 'hasValueOnly'}>
              {handleNewLines(val)}
            </td>
          </tr>
        )
      );
    }
    return isArrayEntry ? (
      html
    ) : (
      <table key={getUniqueId()}>
        <tbody>
          {options.preExtraRows}
          {html}
          {options.postExtraRows}
        </tbody>
      </table>
    );
  }
};

// complexity 12
// eslint-disable-next-line complexity
export const valueToString = (
  value?: any,
  valType = 'string',
  defVal?: any
) => {
  if (value === null || value === undefined) {
    return defVal || '';
  }

  if (typeof value === 'boolean') {
    return (value as boolean).toString();
  }

  if (!value) {
    return String(value);
  }

  if (valType === 'date') {
    if (value === 0 || value === '0') {
      return '';
    }

    const momentDate =
      typeof value === 'number' ? moment.unix(value) : moment.utc(value);
    const localTzAbbrev = moment().tz(moment.tz.guess()).format('z');
    return `${momentDate.format('L LTS')} ${localTzAbbrev}`;
  }

  if (typeof value === 'object') {
    if (isArrayBuffer(value)) {
      return arrayBufferToString(value);
    }

    return JSON.stringify(value);
  }

  return String(value);
};

const arrayBufferToString = (buf) => {
  if (buf.length === 1) {
    // Probably is a bit column
    return String(buf[0]);
  }
  return buf.toString('utf-8');
};

// From: http://stackoverflow.com/a/21799845/1050818
const isArrayBuffer = (value) =>
  value &&
  value.buffer instanceof ArrayBuffer &&
  value.byteLength !== undefined;
